import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { TabItem, TabViewComponent } from '../components/tab-view/tab-view.component';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { CampaignMetricsComponent } from './campaign-metrics/campaign-metrics.component';
import { CampaignStatsComponent } from './campaign-stats/campaign-stats.component';
import { CampaignHeaderComponent } from './campaign-header/campaign-header.component';
import { CampaignToolbarComponent } from './campaign-toolbar/campaign-toolbar.component';
import { CampaignFilterOptions } from './campaign-filter-modal/campaign-filter-modal.component';
import { CampaignTableComponent } from './campaign-table/campaign-table.component';
import { SegmentsMetricsComponent } from './segments-metrics/segments-metrics.component';
import { SegmentsHeaderComponent } from './segments-header/segments-header.component';
import { SegmentsToolbarComponent } from './segments-toolbar/segments-toolbar.component';
import { SegmentsTableComponent } from './segments-table/segments-table.component';
import { AddSegmentModalComponent } from './add-segment-modal/add-segment-modal.component';
import { DeleteSegmentModalComponent } from './delete-segment-modal/delete-segment-modal.component';
import { CampaignService, CampaignFilters } from '../services/campaign.service';
import { ApiService } from '../services/api.service';
import { ProfileService } from '../services/profile.service';
import { LocationService } from '../services/location.service';
import { NotificationService } from '../services/notification.service'; // NotificationService;
import { Segment } from '../shared/types/segment.interface';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  Subject,
  switchMap,
  takeUntil,
  takeWhile,
  timer,
} from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogMutexService } from '../services/dialog-mutex.service';
/**
 * Manages the Campaigns and Segments workspace: provides tabs for campaigns and segments, search, filtering, pagination, view toggles, and modals for creating/editing/deleting segments as well as campaign duplication and navigation for campaign editing.
 * @component
 * @selector app-campaign
 * @example
 *   <app-campaign></app-campaign>
 * @input {none} none - This component does not declare @Input properties; it manages its own state and receives data via injected services and route query params.
 * @output {none} none - This component does not emit @Output events; interactions are handled through services, router navigation, and internal event handlers.
 *
 * Key responsibilities:
 * - Maintain dashboardTabs and activeTab state to switch between "Campaigns" and "Segments".
 * - Manage toolbar state including selectedView, selectedLocation, activeFilters, and searchTerm.
 * - Load and display campaigns and segments with server-side pagination, search and filters.
 * - Control modal states for adding/editing and deleting segments (showAddSegmentModal, showDeleteModal).
 * - Provide actions for creating, editing, duplicating and deleting campaigns and segments (onCreateCampaign, onCreateSegment, onEditSegment, onDuplicateCampaign, onDeleteCampaign, onConfirmDelete).
 * - Handle UI events from tables and toolbars (onSearchChange, onViewChange, onLocationChange, onFiltersApplied, onFiltersCleared, onSegmentsPageChange, onSegmentsRowsPerPageChange).
 *
 * Important methods (high level):
 * - ngOnInit(): Lifecycle init that sets default pagination, wires up debounced search, reads query params to set tab/modal state and triggers initial load of campaigns and segments.
 * - loadCampaigns(): Calls campaignService.getCampaigns with current filters, updates campaigns list, totalRecords and initial-load tracking flags.
 * - loadSegments(): Calls apiService.getSegments with current filters, updates segments list, totalSegmentsRecords and initial-load tracking flags.
 * - loadSegmentForEdit(segmentId): Fetches a single segment for editing and opens the add/edit segment modal.
 * - onCreateSegment(), onEditSegment(segment): Prepare modal state for creating or editing a segment.
 * - onConfirmDelete(segment): Deletes a segment via apiService.deleteSegment and handles success/error toasts and list refresh.
 * - onSaveSegment(segmentData), onCloseAddSegmentModal(), onCloseDeleteModal(): Modal lifecycle handlers to refresh lists and reset modal/edit state.
 * - onFiltersApplied(filters), onFiltersCleared(): Convert UI filter selections to API filter params and reload campaigns.
 * - onSearchChange(term): Debounced search input that resets pagination and triggers loadCampaigns or loadSegments depending on active tab.
 *
 * Injected services and collaborators:
 * - campaignService: CRUD and listing operations for campaigns, plus cache management for statistics.
 * - apiService: Segment-related API operations (getSegments, getSegmentById, deleteSegment).
 * - profileService: Provides locations for mapping human-readable labels to IDs.
 * - messageService: Shows success/error toasts to the user.
 * - route (ActivatedRoute) and router (Router): Read query params for initial state and navigate to editor screens.
 *
 * Notes / Lifecycle behavior:
 * - ngOnInit wires a debounced search Subject (300ms + distinctUntilChanged) that updates filters and reloads data.
 * - Query params are observed to switch to the segments tab or auto-open the create-segment modal.
 * - The component tracks "original" record counts and hasLoadedInitial* flags to support empty-state UX logic.
 */
@Component({
  selector: 'app-campaign',
  standalone: true,
  imports: [
    TabViewComponent,
    CommonModule,
    BaseChartDirective,
    CampaignMetricsComponent,
    CampaignStatsComponent,
    CampaignHeaderComponent,
    CampaignToolbarComponent,
    CampaignTableComponent,
    SegmentsMetricsComponent,
    SegmentsHeaderComponent,
    SegmentsToolbarComponent,
    SegmentsTableComponent,
    AddSegmentModalComponent,
    DeleteSegmentModalComponent,
  ],
  templateUrl: './campaign.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './campaign.component.scss',
})
export class CampaignComponent implements OnInit, OnDestroy {
  dashboardTabs: TabItem[] = [
    {
      name: 'Campaigns',
      // icon: 'assets/images/sidebar/analytics-icon.svg'
    },
    {
      name: 'Segments',
      // icon: 'assets/images/customer/user.svg'
    },
  ];
  activeTab = 'Campaigns';

  // Toolbar properties
  selectedView: 'list' | 'calendar' = 'calendar';
  selectedLocation = '';
  activeFilters: CampaignFilterOptions | null = null;

  // Campaign table properties
  campaigns: any[] = [];
  segments: Segment[] = [];
  isLoading = false;
  currentFilters: CampaignFilters = {};
  totalRecords = 0;
  totalSegmentsRecords = 0;
  originalCampaignCount = 0;
  hasLoadedInitialCampaigns = false;
  originalSegmentCount = 0;
  hasLoadedInitialSegments = false;
  // Search functionality
  searchTerm = '';
  private searchSubject = new Subject<string>();

  // Modal state
  showAddSegmentModal = false;
  showDeleteModal = false;
  segmentToDelete: Segment | null = null;
  segmentToEdit: Segment | null = null;
  isEditMode = false;
  isDeletingSegment = false;

  // Hide stats state
  hideStats = false;

  private destroy$ = new Subject<void>();
  /** Cancels an in-flight upload-job poll when a newer upload starts or the page is destroyed. */
  private uploadPollStop$ = new Subject<void>();

  constructor(
    private campaignService: CampaignService,
    private apiService: ApiService,
    private profileService: ProfileService,
    private locationService: LocationService,
    private messageService: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private dialogMutex: DialogMutexService
  ) {}

  ngOnDestroy(): void {
    this.uploadPollStop$.next();
    this.uploadPollStop$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize component state, set up search debouncing, handle route query parameters, and load initial campaigns and segments.
   * @example
   * gOnInit()
   * // returns void
   * @returns {void} Initializes default pagination, subscribes to debounced search input to update filters and reload data, processes query params for tab selection and segment creation, and triggers initial data loads for campaigns and segments.
   **/
  ngOnInit(): void {
    this.locationService.clearShopLocation();

    // Initialize with default pagination only
    this.currentFilters = {
      page: 1,
      limit: 10,
    };

    // Set up search debouncing
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(searchTerm => {
        this.searchTerm = searchTerm;
        this.currentFilters = {
          ...this.currentFilters,
          page: 1, // Reset to first page when searching
          search: searchTerm, // Add search term to filters
        };

        // Load campaigns or segments based on active tab
        if (this.activeTab === 'Campaigns') {
          this.loadCampaigns();
        } else {
          this.loadSegments();
        }
      });

    // Handle query parameters for tab and modal opening
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const tab = params['tab'];
      const action = params['action'];

      if (tab === 'segments') {
        this.activeTab = 'Segments';
      }

      if (action === 'create-segment') {
        if (this.dialogMutex.isLaneDialogOpen()) {
          this.dialogMutex.defer('feature-modal', () => this.onCreateSegment());
        } else {
          this.onCreateSegment();
        }
      }

      // Load data for the active tab after determining it from query params
      this.loadDataForActiveTab();
    });
  }

  private loadDataForActiveTab() {
    if (this.activeTab === 'Campaigns' && !this.hasLoadedInitialCampaigns) {
      this.loadCampaigns();
    } else if (this.activeTab === 'Segments' && !this.hasLoadedInitialSegments) {
      this.loadSegments();
    }
  }

  onTabChange(tabName: string) {
    this.activeTab = tabName;
    this.loadDataForActiveTab();
  }

  onCreateCampaign() {
    // Handle create campaign action
    // TODO: Implement create campaign functionality
  }

  onCreateSegment() {
    // Handle create segment action
    this.isEditMode = false;
    this.segmentToEdit = null;
    this.showAddSegmentModal = true;
  }

  onEditSegment(segment: any) {
    // Handle edit segment action
    this.isEditMode = true;
    this.loadSegmentForEdit(segment.id);
  }

  /**
   * Load a segment by its ID for editing, toggling loading state and opening the edit modal on success.
   * @example
   * loadSegmentForEdit('segment123')
   * undefined
   * @param {{string}} {{segmentId}} - ID of the segment to load for editing.
   * @returns {{void}} No return value; updates component state and may open the edit modal as a side effect.
   **/
  private loadSegmentForEdit(segmentId: string): void {
    this.isLoading = true;
    this.apiService.getSegmentById(segmentId).subscribe({
      next: response => {
        this.isLoading = false;
        if (response.status && response.data) {
          this.segmentToEdit = response.data;
          this.showAddSegmentModal = true;
        } else {
          console.error('Failed to load segment details:', response.message);
          // TODO: Show error message to user
        }
      },
      error: error => {
        this.isLoading = false;
        console.error('Error loading segment details:', error);
        // TODO: Show error message to user
      },
    });
  }

  onDeleteSegment(segment: any) {
    // Handle delete segment action
    this.loadSegments();
  }

  /**
   * Confirm deletion of a segment: calls API to delete the given segment (if it has an id), handles success/error responses, updates component state, reloads segments, and shows toast notifications.
   * @example
   * nConfirmDelete({ id: 'segmentId123' })
   * undefined
   * @param {{Segment}} {{segment}} - Segment object to delete; must contain a valid id to proceed.
   * @returns {{void}} No return value; function performs side effects (API call, state updates, and UI notifications).
   **/
  onConfirmDelete(segment: Segment): void {
    if (segment && segment.id) {
      this.isDeletingSegment = true;
      this.apiService.deleteSegment(segment.id).subscribe({
        next: response => {
          this.isDeletingSegment = false;
          if (response.status) {
            // Show success toast
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Segment deleted successfully',
            });
            // Reload segments to update the table data
            this.loadSegments();
            this.showDeleteModal = false;
            this.segmentToDelete = null;
          } else {
            console.error('Failed to delete segment:', response.message);
            // Show error toast
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.message || 'Failed to delete segment',
            });
          }
        },
        error: error => {
          this.isDeletingSegment = false;
          console.error('Error deleting segment:', error);
          // Show error toast
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete segment. Please try again.',
          });
        },
      });
    }
  }

  onCloseDeleteModal(): void {
    this.showDeleteModal = false;
    this.segmentToDelete = null;
    this.isDeletingSegment = false;
  }

  onDuplicateSegment(segment: any) {
    // Handle duplicate segment action
    // TODO: Implement duplicate segment functionality
  }

  // Campaign table event handlers
  onEditCampaign(campaign: any) {
    this.router.navigate(['/campaign-editor'], {
      queryParams: { campaignId: campaign.id },
    });
    // TODO: Implement edit campaign functionality
  }

  onDeleteCampaign(campaign: any) {
    // TODO: Implement delete campaign functionality
  }

  /**
   * Duplicate a campaign object, remove its id, set it to draft, and create a new campaign via the campaignService.
   * @example
   * nDuplicateCampaign({ id: 1, name: 'Spring Sale' })
   * void
   * @param {{any}} {{campaign}} - Campaign object to duplicate; id will be removed and the copy will be created in draft status.
   * @returns {{void}} No direct return value; performs an API call to create the duplicated campaign and updates component state (clears stats cache and reloads campaigns).
   **/
  onDuplicateCampaign(campaign: any) {
    const duplicateCampaign = CampaignService.buildDuplicatePayload(campaign);

    // Call the create campaign API
    this.campaignService.createCampaign(duplicateCampaign).subscribe({
      next: response => {
        if (response.status) {
          // Clear statistics cache to refresh stats after creating campaign
          this.campaignService.clearStatisticsCache();
          // Reload campaigns to show the new duplicated campaign
          this.loadCampaigns();
        } else {
          console.error('Failed to duplicate campaign:', response.message);
          // TODO: Show error message to user
        }
      },
      error: error => {
        console.error('Error duplicating campaign:', error);
        // TODO: Show error message to user
      },
    });
  }

  // Load campaigns from API
  /**
   * Loads campaigns from the backend using current filters, pagination and search term, and updates component state (campaigns, totalRecords, loading flags, and originalCampaignCount).
   * @example
   * oadCampaigns()
   * void
   * @returns {void} Triggers an asynchronous request and updates component properties; does not return a value.
   **/
  loadCampaigns(): void {
    this.isLoading = true;

    const filters: CampaignFilters = {
      ...this.currentFilters,
      page: this.currentFilters.page || 1,
      limit: this.currentFilters.limit || 10,
      search: this.searchTerm, // Include search term in API call
    };

    this.campaignService.getCampaigns(filters).subscribe({
      next: response => {
        if (response.status && response.data) {
          this.campaigns = response.data.campaigns;
          this.totalRecords = parseInt(response.data.pagination.total, 10);

          // Track original campaign count for empty state logic
          if (!this.hasLoadedInitialCampaigns) {
            this.originalCampaignCount = this.campaigns.length;
            this.hasLoadedInitialCampaigns = true;
          }
        }
        this.isLoading = false;
      },
      error: error => {
        console.error('Error loading campaigns:', error);
        this.isLoading = false;
      },
    });
  }

  // Load segments from API
  /**
   * Load segments from the API, update component state (segments, pagination, loading flags) and handle errors.
   * @example
   * oadSegments()
   * undefined
   * @returns {{void}} Does not return a value; updates component state and handles loading/error states.
   **/
  loadSegments(options: { quiet?: boolean } = {}): void {
    if (!options.quiet) {
      this.isLoading = true;
    }
    const params = {
      page: this.currentFilters.page || 1,
      limit: this.currentFilters.limit || 10,
      search: this.searchTerm,
      status: '',
    };

    this.apiService.getSegments(params).subscribe({
      next: response => {
        if (response.status && response.data) {
          this.segments = response.data.userSegment;
          this.totalSegmentsRecords = parseInt(response.data.pagination.total, 10);

          // Track original segment count for empty state logic
          if (!this.hasLoadedInitialSegments) {
            this.originalSegmentCount = this.segments.length;
            this.hasLoadedInitialSegments = true;
          }
        }
        this.isLoading = false;
      },
      error: error => {
        console.error('Error loading segments:', error);
        this.isLoading = false;
      },
    });
  }

  // Toolbar event handlers
  onSearchChange(searchTerm: string) {
    this.searchSubject.next(searchTerm);
  }

  onViewChange(view: 'list' | 'calendar') {
    this.selectedView = view;
    // TODO: Implement view change functionality
  }

  /**
   * Updates campaign filters and reloads campaigns when selected location labels change; maps labels to location IDs via ProfileService and resets pagination.
   * @example
   * nLocationChange(['Main Branch', 'East Branch'])
   * undefined
   * @param {{string[]}} {{locationLabels}} - Array of location labels to convert to location IDs and apply as filter.
   * @returns {{void}} Void return; updates component state and triggers campaigns reload.
   **/
  onLocationChange(locationLabels: string[]) {
    // Convert location labels to IDs for API filtering
    if (locationLabels && locationLabels.length > 0) {
      // Get location IDs from labels using ProfileService
      this.profileService.locations$.pipe(takeUntil(this.destroy$)).subscribe(locations => {
        const locationIds = locationLabels
          .map(label => {
            // Find by branch label, or fallback to id if the toolbar emitted an ID
            const location = locations.find(loc => loc.branch === label || loc.id === label);
            return location ? location.id : null;
          })
          .filter(id => id !== null);

        const newLocationId = locationIds.join(',');
        this.selectedLocation = locationIds.length > 0 ? (locationIds[0] as string) : '';
        if (this.selectedLocation) {
          this.locationService.setShopLocationById(this.selectedLocation);
        } else {
          this.locationService.clearShopLocation();
        }

        // Only reload if the filter actually changed
        if (this.currentFilters.location_id !== newLocationId) {
          // Update filters with location IDs
          this.currentFilters = {
            ...this.currentFilters,
            page: 1, // Reset to first page when changing location
            location_id: newLocationId,
          };

          // Reload data with new location filter based on active tab
          if (this.activeTab === 'Campaigns') {
            this.loadCampaigns();
          } else {
            this.loadSegments();
          }
        }
      });
    } else {
      this.selectedLocation = '';
      this.locationService.clearShopLocation();
      const { location_id: _locationId, ...filtersWithoutLocation } = this.currentFilters;

      if (_locationId !== undefined) {
        this.currentFilters = {
          ...filtersWithoutLocation,
          page: 1, // Reset to first page when clearing location
        };

        // Reload data without location filter based on active tab
        if (this.activeTab === 'Campaigns') {
          this.loadCampaigns();
        } else {
          this.loadSegments();
        }
      }
    }
  }

  onFilterClick() {
    // TODO: Implement filter functionality
  }

  /**
   * Apply provided campaign filters, convert them into API-ready query parameters, reset pagination, and reload campaigns.
   * @example
   * nFiltersApplied({ campaignTypes: ['email','social'], statuses: ['active'], locations: [1,2] })
   * undefined
   * @param {{CampaignFilterOptions}} {{filters}} - Filter options to apply to the campaigns view.
   * @returns {{void}} No return value; updates component state and triggers campaign reload.
   **/
  onFiltersApplied(filters: CampaignFilterOptions) {
    this.activeFilters = filters;
    if (!filters.locations?.length) {
      this.locationService.clearShopLocation();
    } else {
      this.locationService.setShopLocationById(String(filters.locations[0]));
    }

    // Convert filter options to API filters with comma-separated values
    const apiFilters: CampaignFilters = {};

    if (filters.campaignTypes && filters.campaignTypes.length > 0) {
      apiFilters.platform = filters.campaignTypes.join(',');
    }

    if (filters.statuses && filters.statuses.length > 0) {
      apiFilters.campaign_status = filters.statuses.join(',');
    }

    if (filters.locations && filters.locations.length > 0) {
      apiFilters.location_id = filters.locations.join(',');
    }

    this.currentFilters = {
      page: 1, // Reset to first page when applying filters
      limit: 10,
      search: this.searchTerm, // Include current search term
      ...apiFilters,
    };

    // Reload campaigns when filters are applied
    this.loadCampaigns();
  }

  onLocationSelectionFromModal(locationLabels: string[]) {
    // TODO: Implement location selection from modal
  }

  // Modal event handlers
  onCloseAddSegmentModal() {
    this.showAddSegmentModal = false;
    this.isEditMode = false;
    this.segmentToEdit = null;
  }

  onSaveSegment(segmentData: any) {
    // Reload segments to update the table data (upload rows may still show 0 until job finishes)
    this.loadSegments();
    this.showAddSegmentModal = false;
    this.isEditMode = false;
    this.segmentToEdit = null;

    const segmentId = segmentData?.segment?.id;
    const uploadStatus = String(segmentData?.upload_status || '').toLowerCase();
    if (segmentId && (uploadStatus === 'pending' || uploadStatus === 'processing')) {
      this.pollSegmentUploadJob(String(segmentId));
    }
  }

  /**
   * Poll GET /segment/:id/upload-job until ready/failed, then refresh list counts.
   */
  private pollSegmentUploadJob(segmentId: string): void {
    this.uploadPollStop$.next();
    let attempts = 0;
    const maxAttempts = 30;

    timer(1500, 2000)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.uploadPollStop$),
        switchMap(() =>
          this.apiService.get(`segment/${segmentId}/upload-job`).pipe(catchError(() => of(null)))
        ),
        takeWhile(response => {
          attempts += 1;
          const status = String(response?.data?.status || '').toLowerCase();
          const done = status === 'ready' || status === 'failed';
          return !(done || attempts >= maxAttempts);
        }, true)
      )
      .subscribe({
        next: response => {
          const status = String(response?.data?.status || '').toLowerCase();
          if (status === 'ready') {
            this.loadSegments({ quiet: true });
            this.messageService.add({
              severity: 'success',
              summary: 'Upload complete',
              detail: 'Segment users are ready.',
            });
            return;
          }
          if (status === 'failed') {
            this.loadSegments({ quiet: true });
            this.messageService.add({
              severity: 'error',
              summary: 'Upload failed',
              detail: response?.data?.errorMessage || 'Segment upload processing failed.',
            });
            return;
          }
          if (attempts >= maxAttempts) {
            this.loadSegments({ quiet: true });
            this.messageService.add({
              severity: 'warn',
              summary: 'Upload still processing',
              detail:
                'Segment upload is taking longer than expected. Refresh to see the latest counts.',
            });
          }
        },
      });
  }

  // Segments table pagination handlers
  onSegmentsPageChange(event: any) {
    this.currentFilters = {
      ...this.currentFilters,
      page: event.page + 1, // PrimeNG uses 0-based indexing, API uses 1-based
      limit: event.rows,
    };
    this.loadSegments();
  }

  onSegmentsRowsPerPageChange(event: any) {
    this.currentFilters = {
      ...this.currentFilters,
      page: 1, // Reset to first page when changing rows per page
      limit: event.value,
    };
    this.loadSegments();
  }

  // Hide stats event handler
  onHideStatsChange(hideStats: boolean) {
    this.hideStats = hideStats;
  }
}
